import { useState } from "react";
import { toast } from "react-toastify";
import { Columns3, Download, Printer } from "lucide-react";

import { Button } from "../../components/ui/button";
import { ErrorBanner } from "../../features/web3/hyperliquid/components/ErrorBanner";
import { QueueLayout } from "../../features/web3/forex/components/QueueLayout";
import { QueueTable, Td } from "../../features/web3/forex/components/table";
import { formatDateTime } from "../../lib/format";
import { FilterBar, FilterField, Money, PAGE_SIZE_OPTIONS, PagePager, orDash } from "../forex/parts";
import { accountLabel, downloadText, getText, printHtml, useLedger, useLedgerCan, useLookups } from "./api";

const EMPTY = {
  fiscalYear: "",
  startDate: "",
  endDate: "",
  account: "",
  ledger: "",
  group: "",
  currency: "",
  costCenter: "",
  receiptType: "",
  pageSize: "50",
};

// Every field a row carries, except the raw ids (shown as names instead),
// signedAmount (equal to amount), and the line description and line number.
// `key` is the backend export column(s) behind each one.
const columns = (lk, base) => [
  { label: "Date", key: "date", value: (r) => r.date },
  { label: "Receipt #", key: "number", value: (r) => r.number },
  { label: "Type", key: "receiptType", value: (r) => r.receiptType },
  { label: "Source", key: "source", value: (r) => r.source },
  { label: "Account", key: "accountCode,accountName", value: (r) => `${r.accountCode} · ${r.accountName}` },
  { label: "Ledger", key: "ledger", value: (r) => accountLabel(lk.ledger[r.ledger]) },
  { label: "Group", key: "group", value: (r) => lk.options?.groups.find((g) => g.id === r.group)?.name ?? r.group },
  { label: "Cost center", key: "costCenter", value: (r) => lk.costCenter[r.costCenter]?.name ?? "" },
  { label: "Currency", key: "currency", value: (r) => r.currency },
  { label: "Rate", key: "rate", value: (r) => r.rate, align: "right", mono: true },
  { label: "Amount", key: "amount", value: (r) => r.amount, align: "right", money: true },
  { label: "Debit", key: "debit", value: (r) => r.debit, align: "right", mono: true },
  { label: "Credit", key: "credit", value: (r) => r.credit, align: "right", mono: true },
  { label: `Running balance (${base})`, key: "runningBalance", value: (r) => r.runningBalance, align: "right", money: true },
  { label: "Running currency amount", key: "runningCurrencyAmount", value: (r) => r.runningCurrencyAmount, align: "right", money: true },
  { label: "Description", key: "description", value: (r) => r.description },
  { label: "Created by", key: "createdBy", value: (r) => r.createdBy },
];

function Total({ label, value }) {
  return (
    <div className="space-y-1 rounded-lg border border-border/70 bg-white/[0.02] p-4">
      <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">{label}</div>
      <div className="text-xl">
        <Money value={value} />
      </div>
    </div>
  );
}

export default function TrustLedgerStatement() {
  const lk = useLookups();
  const [draft, setDraft] = useState(EMPTY);
  const [applied, setApplied] = useState(EMPTY);
  const [page, setPage] = useState(1);
  // ponytail: column choice lives for the visit; persist it if admins ask.
  const [hidden, setHidden] = useState([]);
  const [picking, setPicking] = useState(false);
  const [exporting, setExporting] = useState(null);
  const canExport = useLedgerCan()("GET", "/reports/account/export");

  const fiscalYear = applied.fiscalYear || lk.openYear?._id;
  const params = { ...applied, fiscalYear };
  const q = useLedger(fiscalYear ? "/reports/account" : null, { ...params, page });
  const data = q.data;
  // The report names the base currency it was built in; until it answers, the options' one.
  const base = data?.baseCurrency ?? lk.baseCurrency;
  const allCols = columns(lk, base);
  const cols = allCols.filter((c) => !hidden.includes(c.label));
  const toggle = (label) =>
    setHidden((h) => (h.includes(label) ? h.filter((x) => x !== label) : h.length < allCols.length - 1 ? [...h, label] : h));

  // The backend builds the file over the whole selection, with a TOTAL row
  // (CSV) or a print-ready page (HTML), in the columns picked here.
  const exportAs = async (format) => {
    setExporting(format);
    try {
      const { page: _p, pageSize: _s, ...filters } = params;
      const text = await getText("/reports/account/export", { ...filters, format, columns: cols.map((c) => c.key).join(",") });
      if (format === "csv") downloadText(text, `account-statement-${data?.fiscalYear?.title ?? "ledger"}.csv`, "text/csv;charset=utf-8");
      else printHtml(text);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setExporting(null);
    }
  };

  const select = (label, name, options) => (
    <FilterField
      label={label}
      name={name}
      options={[{ value: "", label: "All" }, ...options]}
      draft={draft}
      setDraft={setDraft}
    />
  );

  return (
    <QueueLayout
      eyebrow="Trust Ledger"
      title="Account"
      accent="statement"
      description={`Every posted line matching the filters, oldest first, with running balances in ${base} and in each line's own currency.`}
      isFetching={q.isFetching}
      onRefresh={() => q.refetch()}
    >
      <FilterBar
        onApply={() => {
          setApplied(draft);
          setPage(1);
        }}
        onReset={() => {
          setDraft(EMPTY);
          setApplied(EMPTY);
          setPage(1);
        }}
      >
        <FilterField
          label="Fiscal year"
          name="fiscalYear"
          options={[
            { value: "", label: "Open year" },
            ...lk.years.map((y) => ({ value: y._id, label: `${y.title} (${y.status})` })),
          ]}
          draft={draft}
          setDraft={setDraft}
        />
        <FilterField label="From date" name="startDate" type="date" draft={draft} setDraft={setDraft} />
        <FilterField label="To date" name="endDate" type="date" draft={draft} setDraft={setDraft} />
        {select("Account", "account", lk.postable.map((a) => ({ value: a._id, label: accountLabel(a) })))}
        {select("Ledger", "ledger", lk.ledgers.map((l) => ({ value: l._id, label: accountLabel(l) })))}
        {select("Group", "group", (lk.options?.groups ?? []).map((g) => ({ value: String(g.id), label: g.name })))}
        {select("Currency", "currency", lk.currencies.map((c) => ({ value: c.name, label: c.name })))}
        {select("Cost center", "costCenter", lk.costCenters.map((c) => ({ value: c._id, label: `${c.code} · ${c.name}` })))}
        {select("Type", "receiptType", (lk.options?.receiptTypes ?? []).map((t) => ({ value: t, label: t })))}
        <FilterField label="Page size" name="pageSize" options={PAGE_SIZE_OPTIONS} draft={draft} setDraft={setDraft} />
      </FilterBar>

      {q.error ? (
        <ErrorBanner error={q.error} onRetry={() => q.refetch()} />
      ) : (
        <>
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs text-muted-foreground">
                {data ? `${data.fiscalYear.title} · base currency ${data.baseCurrency}. ${data.balanceBasis}` : ""}
              </p>
              <div className="relative flex gap-2">
                <Button size="sm" onClick={() => setPicking((x) => !x)}>
                  <Columns3 className="h-3.5 w-3.5" /> Columns
                </Button>
                {canExport && (
                  <>
                    <Button size="sm" onClick={() => exportAs("csv")} disabled={!!exporting || !data?.totalRows}>
                      <Download className="h-3.5 w-3.5" /> {exporting === "csv" ? "Exporting…" : "Export CSV"}
                    </Button>
                    <Button size="sm" onClick={() => exportAs("html")} disabled={!!exporting || !data?.totalRows}>
                      <Printer className="h-3.5 w-3.5" /> {exporting === "html" ? "Preparing…" : "Print / PDF"}
                    </Button>
                  </>
                )}
                {picking && (
                  <div className="absolute right-0 top-full z-20 mt-1 w-60 space-y-1 rounded-md border border-border/70 bg-background p-3 shadow-lg">
                    {allCols.map((c) => (
                      <label key={c.label} className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={!hidden.includes(c.label)} onChange={() => toggle(c.label)} />
                        {c.label}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <Total label={`Debit (${base})`} value={data?.totals.debit} />
              <Total label={`Credit (${base})`} value={data?.totals.credit} />
              <Total label={`Balance (${base})`} value={data?.totals.balance} />
            </div>
            {data?.totals.currencies.length > 0 && (
              <QueueTable
                columns={[
                  { label: "Currency" },
                  { label: "Amount", align: "right" },
                  { label: `Debit (${base})`, align: "right" },
                  { label: `Credit (${base})`, align: "right" },
                  { label: `Carrying (${base})`, align: "right" },
                ]}
                rows={data.totals.currencies}
                renderRow={(c) => (
                  <tr key={c.currency}>
                    <Td>{c.currency}</Td>
                    <Td align="right"><Money value={c.amount} /></Td>
                    <Td align="right" className="font-mono">{c.debit}</Td>
                    <Td align="right" className="font-mono">{c.credit}</Td>
                    <Td align="right"><Money value={c.carryingBase ?? c.carryingUsdt} /></Td>
                  </tr>
                )}
              />
            )}
            <p className="text-xs text-muted-foreground">Totals cover the whole filtered selection, not this page.</p>
          </div>

          <QueueTable
            columns={cols}
            isLoading={q.isLoading || !fiscalYear}
            rows={data?.data}
            emptyText="No ledger lines match."
            renderRow={(r) => (
              <tr key={`${r.receiptId}-${r.lineIndex}`} className="hover:bg-white/[0.02]">
                {cols.map((c) => {
                  const v = c.value(r);
                  return (
                    <Td key={c.label} align={c.align} className={c.mono ? "font-mono" : "whitespace-nowrap text-xs"}>
                      {c.label === "Date" ? formatDateTime(v) : c.money ? <Money value={v} /> : orDash(v)}
                    </Td>
                  );
                })}
              </tr>
            )}
          />
          <PagePager data={data} onChange={setPage} label="lines" />
        </>
      )}
    </QueueLayout>
  );
}
